export const spacePlanningEn = {
  pageTitle: "Space Planning",
  pageSubtitle:
    "Review, edit and approve pending planograms, view active planograms, or browse historical records",

  banner: {
    title: "Bring Your Planograms to Life",
    description:
      "Manage the full planogram lifecycle in one place. Review pending planograms generated from your previous assortment simulations, validate their performance metrics, and edit them as needed before approving for activation — or monitor active planograms across all your stores. Use filters and bulk actions to streamline your workflow and move faster from decision to deployment.",
  },

  tabs: {
    pending: "Pending Planograms",
    active: "Active Planograms",
    historical: "Historical Planograms",
  },

  filters: {
    viewOn: "View on:",
    allChains: "All Chains",
    allStores: "All Stores",
    allCategories: "All Categories",
    searchPlaceholder: "Search by store, ID, or SIM-id...",
    filterPogs: "Filter store POGs by:",
    latest: "Latest",
    oldest: "Oldest",
    activeFilters: "Active filters:",
    clearAll: "Clear all",
    commercialChain: "Commercial Chain",
    store: "Store",
    category: "Category",
    assortment: "Assortment",
    search: "Search",
    searchButton: "Search",
  },

  pagination: {
    rowsPerPage: "Rows per page:",
  },

  results: {
    showing: "Showing {{start}}-{{end}} of {{total}} stores",
    selected: "({{count}} selected)",
  },

  columns: {
    storeCode: "Store Code",
    pogName: "POG Name",
    storeStatus: "Store Status",
    pogId: "POG ID",
    category: "Category",
    assortment: "Assortment",
    returnOnSpace: "Return on Space",
    coreRange: "Core Range",
    shelfAlignment: "Shelf Alignment",
    dogms: "DOGMS",
    size: "Size",
    warnings: "Warnings",
    lastEdited: "Last Edited",
    archivedAt: "Archived",
    actions: "Actions",
    view: "View",
    analysis: "Analysis",
    logs: "Logs",
  },

  status: {
    pending: "Pending",
    active: "Active",
    historical: "Historical",
    notAvailable: "N/A",
    loading: "Loading planograms...",
    loadError: "Unable to load planograms.",
    loadErrorToastTitle: "Failed to load space planning data",
    loadErrorToastMessage: "Could not load these planograms: {{sources}}.",
    processing: "Processing...",
    error: "Processing error",
    updateFailed: "Update failed",
    updateFailedDetail:
      "The update API ran but none of the approved SKU inserts, removals, or swaps were applied. The planogram reflects the parent layout, not the approved changes.",
    updatePartial: "Partially applied",
    updatePartialDetail:
      "The update API ran and some of the approved SKU inserts, removals, or swaps were applied. Others were skipped (no compatible shelf, PNL impact, or other Frogger constraints).",
  },

  actions: {
    update: "Update",
    approve: "Approve",
    edit: "Edit",
    viewEdit: "View / Edit",
    updateSelected: "Update Selected ({{count}})",
    approveSelected: "Approve Selected ({{count}})",
    deleteSelected: "Delete Selected ({{count}})",
    violations: "Violations ({{count}})",
    reports: "Reports ({{count}})",
    storeIntegrityReport: "Store Integrity Report",
    categoryRangeReviewReport: "Category Range Review Report",
    export: "Export ({{count}})",
  },

  toasts: {
    approvalsSuccess: "Planograms approved",
    approvalsError: "Failed to approve planograms",
    missingPogReference: "Missing merchflow/POG reference for this row",
    pogDataUnavailable: "POG data unavailable",
  },

  noResults: "No planograms found",

  logs: {
    title: "Activity Logs",
    exportLogs: "Export Logs",
    entries: {
      activated: "Planogram Activated",
      analysisRun: "Performance Analysis Run",
      compliancePassed: "Compliance Check Passed",
      warningDetected: "Warning Detected",
      auditCompleted: "Store Audit Completed",
    },
  },

  preview: {
    title: "Planogram Preview",
    category: "Category",
    assortment: "Assortment",
    activeDate: "Active Date",
    size: "Size",
    segments: "{{count}} segments",
    performanceMetrics: "Performance Metrics",
    status: "Status",
    returnOnSpace: "Return on Space",
    coreRange: "Core Range",
    shelfAlignment: "Shelf Alignment",
    dogms: "DOGMS",
    planogramLayout: "Planogram Layout",
    shelf: "Shelf {{number}}",
    section: "{{brand}} Section",
    totalSegments: "Total Segments",
    totalSkus: "Total SKUs",
    close: "Close",
    export: "Export",
    editPlanogram: "Edit Planogram",
  },

  history: {
    title: "Planogram History",
    approvedBy: "Approved by:",
    activated: "Activated:",
    deactivated: "Deactivated:",
    close: "Close",
    exportHistory: "Export History",
  },

  confirmUpdate: {
    singleTitle: "Confirm Planogram Update",
    singleSubtitle: "Review the changes before proceeding",
    bulkTitle: "Confirm Bulk Update",
    bulkSubtitle: "You are about to update {{count}} planograms",
    bulkSummary: "Bulk Update Summary",
    bulkSummaryDesc: "{{count}} planograms will be activated",
    whatWillHappen: "What will happen:",
    step1Title: "Activate new planogram",
    step1Desc: "Version {{id}} will be set as Active",
    step2Title: "Update store planogram",
    step2Desc: "The planogram will be marked as approved and live in Planograms",
    step3Title: "Archive previous version",
    step3Desc: "The current active planogram will be archived (if any)",
    step4Title: "Remove from pending",
    step4Desc: "This planogram will be removed from the Space Planning queue",
    planogramDetails: "Planogram Details",
    categoryLabel: "Category:",
    sizeLabel: "Size:",
    sizeSegments: "{{count}} segments",
    activeDateLabel: "Active Date:",
    endDateLabel: "End Date:",
    selectedPlanograms: "Selected Planograms ({{count}}):",
    warningTitle: "This action cannot be undone",
    warningDesc:
      "Once activated, this planogram will immediately replace the current one and affect store operations.",
    warningDescBulk:
      "Once activated, these {{count}} planograms will immediately replace current ones and affect store operations.",
    cancel: "Cancel",
    confirm: "Confirm Update",
    confirmBulk: "Confirm Update ({{count}})",
    na: "N/A",
  },

  confirmDelete: {
    title: "Delete Selected Planograms",
    subtitle: "You are about to delete {{count}} pending planogram(s)",
    selectedPlanograms: "Selected Planograms ({{count}}):",
    warningTitle: "This action cannot be undone",
    warningDesc:
      "These {{count}} planogram(s) will be removed from the Space Planning queue. Planograms that are currently processing will be skipped.",
    cancel: "Cancel",
    confirm: "Delete Selected ({{count}})",
    toasts: {
      deletedWithSkipped: "Deleted {{deleted}} planogram(s), skipped {{skipped}} in progress",
      deleted: "Planograms deleted",
      failed: "Failed to delete planograms",
      couldNotDelete: "Could not delete {{count}} planogram(s) — missing planogram reference",
    },
  },
} as const;

export type SpacePlanningTranslations = typeof spacePlanningEn;
